import { sectionRenderer } from '@theme/section-renderer';
import { scrollTo as themeScrollTo } from '@theme/scroll-container';

const LOCAL_FIRST_PARAM = 'local_first';
const LOCAL_VIRTUAL_PAGE_PARAM = 'local_page';
const LOCAL_STOCK_FILTER_PARAM = 'filter.p.m.custom.has_local_stock';
const PRODUCT_IMAGE_FILTER_PARAM = 'filter.p.m.custom.has_product_image';

const IMAGE_FIRST_PARAM = 'image_first';
const IMAGE_VIRTUAL_PAGE_PARAM = 'image_page';

const IMAGE_TRUE_COLLECTION_HANDLE =
  'system-image-priority-with-image';
const IMAGE_FALSE_COLLECTION_HANDLE =
  'system-image-priority-without-image';

const RETURN_CACHE_KEY = 'ostuklikk-local-return-v1:' + import.meta.url;
const RETURN_CACHE_TTL = 10 * 60 * 1000;

const LOCAL_FIRST_VALUE = '1';
const LOCAL_TRUE_VALUE = 'true';
const LOCAL_FALSE_VALUE = 'false';

class LocalStockPriorityToggle extends HTMLElement {
  #sectionObserver = null;
  #refreshQueued = false;
  #initToken = 0;
  #internalMutation = false;

  /*
   * Local-first on kasutaja valitud REŽIIM, mitte local/remote faas.
   * See lipp hoiab nupu aktiivsena ka siis, kui combined nimekiri
   * jõuab kohalike toodete juurest vahelao toodeteni.
   */
  #modeActive = false;

  #pageSize = 0;
  #localCount = 0;
  #remoteCount = 0;
  #phaseCounts = new Map();
  #totalCount = 0;
  #totalPages = 0;

  #pageCache = new Map();
  #warmingPages = new Set();
  #combinedPageCache = new Map();

  #onClick = (event) => {
    event.preventDefault();

    const enabled = !this.#isEnabled();

    this.#renderAllStates(enabled);

    if (enabled) {
      const url = this.#getBaseUrl();

      url.searchParams.set(
        LOCAL_FIRST_PARAM,
        LOCAL_FIRST_VALUE
      );

      url.searchParams.set(
        LOCAL_VIRTUAL_PAGE_PARAM,
        '1'
      );

      history.pushState(
        {},
        '',
        url.toString()
      );

      const controller =
        this.#getController();

      if (controller) {
        controller.#modeActive = true;
        controller.#forceActiveState();
        controller.#resetPageCache();
        controller.#syncForms();

        controller.#initManualPagination({
          scrollToResults: false,
        });
      }
    } else {
      const url = this.#getBaseUrl();

      if (url.pathname.endsWith('/search')) {
        url.searchParams.set(
          PRODUCT_IMAGE_FILTER_PARAM,
          LOCAL_TRUE_VALUE
        );
      }

      window.location.assign(url.toString());
    }
  };

  #onPopState = () => {
    if (!this.#isController()) return;

    const enabled =
      this.#urlSaysEnabled();

    this.#renderAllStates(
      enabled
    );

    document.documentElement.classList.toggle(
      'local-stock-priority-active',
      enabled
    );

    if (!enabled) {
      return;
    }

    this.#syncForms();
    this.#forceActiveState();

    const grid = this.#getSection()?.querySelector('[ref="grid"]');
    if (grid?.dataset.localPriorityUrl === this.#returnUrl()) {
      this.#finishInitialLoad();
      return;
    }

    this.#initManualPagination({
      scrollToResults: false,
    });
  };

  connectedCallback() {
    this.addEventListener(
      'click',
      this.#onClick
    );

    this.#modeActive =
      this.#urlSaysEnabled();

    this.#renderState(
      this.#modeActive
    );

    if (!this.#isController()) {
      return;
    }

    window.addEventListener(
      'popstate',
      this.#onPopState
    );

    this.#ensureStyles();
    this.#observeSectionChanges();

    if (this.#isEnabled()) {
      document.documentElement.classList.add(
        'local-stock-priority-active'
      );

      this.#forceActiveState();

      /*
       * Eelmiste versioonide URL võib sisaldada:
       * page=...
       * filter.p.m.custom.has_local_stock=true/false
       *
       * Uues versioonis EI hoita neid browseri URL-is.
       * Kui need on veel alles, tee üks puhas reload phase-free URL-ile.
       * See tagab ka õige products-per-page tuvastuse.
       */
      const currentUrl =
        new URL(window.location.href);

      if (
        currentUrl.searchParams.has('page') ||
        currentUrl.searchParams.has(
          LOCAL_STOCK_FILTER_PARAM
        ) ||
        currentUrl.searchParams.has(
          PRODUCT_IMAGE_FILTER_PARAM
        ) ||
        currentUrl.searchParams.has(
          IMAGE_FIRST_PARAM
        ) ||
        currentUrl.searchParams.has(
          IMAGE_VIRTUAL_PAGE_PARAM
        )
      ) {
        const cleanUrl =
          this.#getBaseUrl();

        cleanUrl.searchParams.set(
          LOCAL_FIRST_PARAM,
          LOCAL_FIRST_VALUE
        );

        cleanUrl.searchParams.set(
          LOCAL_VIRTUAL_PAGE_PARAM,
          String(this.#getVirtualPage())
        );

        window.location.replace(
          cleanUrl.toString()
        );

        return;
      }

      this.#syncForms();

      if (this.#restoreReturnPage()) return;

      queueMicrotask(() => {
        this.#initManualPagination({
          scrollToResults: false,
        });
      });
    } else {
      this.#finishInitialLoad();
    }
  }

  disconnectedCallback() {
    this.removeEventListener(
      'click',
      this.#onClick
    );

    if (this.#isController()) {
      window.removeEventListener(
        'popstate',
        this.#onPopState
      );
    }

    this.#sectionObserver?.disconnect();
    this.#sectionObserver = null;

    if (this.#isController()) {
      this.#finishInitialLoad();
      document.documentElement.classList.remove(
        'local-stock-priority-active',
        'local-stock-priority-loading'
      );
    }
  }

  #returnUrl() {
    const url = this.#getBaseUrl();
    url.searchParams.set(LOCAL_FIRST_PARAM, LOCAL_FIRST_VALUE);
    url.searchParams.set(LOCAL_VIRTUAL_PAGE_PARAM, String(this.#getVirtualPage()));
    url.searchParams.delete('preview_theme_id');
    url.searchParams.sort();
    return url.pathname + url.search;
  }

  #saveReturnPage(grid) {
    const url = this.#returnUrl();
    grid.dataset.localPriorityUrl = url;
    try {
      sessionStorage.setItem(RETURN_CACHE_KEY, JSON.stringify({
        url,
        time: Date.now(),
        html: grid.innerHTML,
        pageSize: this.#pageSize,
        localCount: this.#localCount,
        remoteCount: this.#remoteCount,
        totalCount: this.#totalCount,
        totalPages: this.#totalPages,
        phaseCounts: [...this.#phaseCounts],
      }));
    } catch {}
  }

  #restoreReturnPage() {
    if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') return false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(RETURN_CACHE_KEY) || 'null');
      if (!saved || !saved.html || saved.url !== this.#returnUrl() ||
          Date.now() - saved.time > RETURN_CACHE_TTL ||
          !Number.isInteger(saved.totalPages) || saved.totalPages < 1) return false;
      const resultsList = this.#getSection()?.querySelector('results-list');
      const grid = resultsList?.querySelector('[ref="grid"]');
      if (!(grid instanceof HTMLUListElement)) return false;
      const template = document.createElement('template');
      template.innerHTML = saved.html;
      const cards = [...template.content.children];
      if (!cards.length || cards.some(card => !card.matches('[ref="cards[]"]'))) return false;
      this.#pageSize = saved.pageSize;
      this.#localCount = saved.localCount;
      this.#remoteCount = saved.remoteCount;
      this.#totalCount = saved.totalCount;
      this.#totalPages = saved.totalPages;
      this.#phaseCounts = new Map(saved.phaseCounts);
      this.#internalMutation = true;
      this.#disableNativeResultsPagination(resultsList);
      this.#setCombinedCount(this.#totalCount);
      this.#renderCombinedPage(resultsList, grid, cards, this.#getVirtualPage());
      grid.dataset.localPriorityRestored = 'history';
      this.#setLoading(false);
      return true;
    } catch {
      this.#internalMutation = false;
      return false;
    }
  }

  #getSection() {
    return this.closest(
      '.shopify-section'
    );
  }

  #getController() {
    const section =
      this.#getSection();

    const controller =
      section?.querySelector(
        'local-stock-priority-toggle'
      );

    return controller instanceof
      LocalStockPriorityToggle
      ? controller
      : null;
  }

  #isController() {
    return (
      this.#getController() === this
    );
  }

  #urlSaysEnabled() {
    return (
      new URL(
        window.location.href
      ).searchParams.get(
        LOCAL_FIRST_PARAM
      ) === LOCAL_FIRST_VALUE
    );
  }

  #isEnabled() {
    /*
     * URL on reloadi/popi järel algallikas.
     * Sama lehe AJAX-paginationi ajal hoiab #modeActive olekut stabiilsena,
     * isegi kui mõni Shopify komponent ajutiselt history URL-i ümber kirjutab.
     */
    return (
      this.#modeActive ||
      this.#urlSaysEnabled()
    );
  }

  #forceActiveState() {
    if (!this.#isEnabled()) {
      return;
    }

    this.#modeActive = true;

    document.documentElement.classList.add(
      'local-stock-priority-active'
    );

    this.#renderAllStates(true);
  }

  #getVirtualPage() {
    const url =
      new URL(
        window.location.href
      );

    const value =
      Number.parseInt(
        url.searchParams.get(
          LOCAL_VIRTUAL_PAGE_PARAM
        ) || '1',
        10
      );

    return (
      Number.isInteger(value) &&
      value > 0
    )
      ? value
      : 1;
  }

  #renderState(
    enabled = this.#isEnabled()
  ) {
    const button =
      this.querySelector(
        '[data-local-stock-priority-button]'
      );

    if (
      !(button instanceof
        HTMLButtonElement)
    ) {
      return;
    }

    button.classList.toggle(
      'is-active',
      enabled
    );

    button.setAttribute(
      'aria-pressed',
      enabled ? 'true' : 'false'
    );

    const state =
      button.querySelector(
        '[data-local-stock-priority-state]'
      );

    if (
      state instanceof HTMLElement
    ) {
      state.textContent =
        enabled
          ? state.dataset.onLabel ||
            'On'
          : state.dataset.offLabel ||
            'Off';
    }
  }

  #renderAllStates(enabled) {
    const section =
      this.#getSection();

    const scope =
      section || document;

    scope
      .querySelectorAll(
        'local-stock-priority-toggle'
      )
      .forEach((toggle) => {
        if (
          toggle instanceof
          LocalStockPriorityToggle
        ) {
          toggle.#modeActive =
            Boolean(enabled);

          toggle.#renderState(
            enabled
          );
        }
      });
  }

  /**
   * Tagastab URL-i, kus on alles KÕIK Shopify päris filtrid:
   * - sort_by
   * - hind
   * - vendor
   * - product type
   * - q
   * - muud filter.* parameetrid
   *
   * Eemaldatakse ainult meie tehnilised parameetrid.
   */
  #getBaseUrl() {
    const url =
      new URL(
        window.location.href
      );

    url.searchParams.delete(
      'page'
    );

    url.searchParams.delete(
      LOCAL_FIRST_PARAM
    );

    url.searchParams.delete(
      LOCAL_VIRTUAL_PAGE_PARAM
    );

    url.searchParams.delete(
      LOCAL_STOCK_FILTER_PARAM
    );

    url.searchParams.delete(
      PRODUCT_IMAGE_FILTER_PARAM
    );

    url.searchParams.delete(
      IMAGE_FIRST_PARAM
    );

    url.searchParams.delete(
      IMAGE_VIRTUAL_PAGE_PARAM
    );

    url.searchParams.delete(
      'local_phase'
    );

    url.searchParams.delete(
      'image_phase'
    );

    return url;
  }

  #resetPageCache() {
    this.#pageCache.clear();
    this.#warmingPages.clear();
    this.#combinedPageCache.clear();
  }

  #getCacheKey(
    phase,
    page
  ) {
    return [
      this.#getBaseUrl().toString(),
      phase.key,
      page,
    ].join('|');
  }

  #hasExactProductMatch() {
    return (
      this.#getSection()
        ?.querySelector(
          'image-priority-sorter'
        )
        ?.dataset
        ?.exactProductMatch ===
      'true'
    );
  }

  #usesImagePriority() {
    const url =
      new URL(
        window.location.href
      );

    const sortBy =
      url.searchParams.get(
        'sort_by'
      );

    return (
      !this.#hasExactProductMatch() &&
      (
        !sortBy ||
        sortBy ===
          'created-descending'
      )
    );
  }

  #getPhases() {
    if (this.#hasExactProductMatch()) {
      return [
        {
          key: 'exact-native',
          localValue: null,
          imageValue: LOCAL_TRUE_VALUE,
        },
      ];
    }

    if (!this.#usesImagePriority()) {
      return [
        {
          key: 'local',
          localValue: LOCAL_TRUE_VALUE,
          imageValue: LOCAL_TRUE_VALUE,
        },
        {
          key: 'remote',
          localValue: LOCAL_FALSE_VALUE,
          imageValue: LOCAL_TRUE_VALUE,
        },
      ];
    }

    return [
      {
        key: 'image-local',
        localValue: LOCAL_TRUE_VALUE,
        imageValue: LOCAL_TRUE_VALUE,
      },
      {
        key: 'image-remote',
        localValue: LOCAL_FALSE_VALUE,
        imageValue: LOCAL_TRUE_VALUE,
      },
    ];
  }

  #getPhaseUrl(phase) {
    const url =
      this.#getBaseUrl();

    if (phase.imageValue !== null) {
      const allProductsMatch =
        url.pathname.match(
          /^(.*\/collections\/)all\/?$/
        );

      if (allProductsMatch) {
        const handle =
          phase.imageValue ===
          LOCAL_TRUE_VALUE
            ? IMAGE_TRUE_COLLECTION_HANDLE
            : IMAGE_FALSE_COLLECTION_HANDLE;

        url.pathname =
          `${allProductsMatch[1]}${handle}`;
      } else {
        url.searchParams.set(
          PRODUCT_IMAGE_FILTER_PARAM,
          phase.imageValue
        );
      }
    }

    if (phase.localValue !== null) {
      url.searchParams.set(
        LOCAL_STOCK_FILTER_PARAM,
        phase.localValue
      );
    }

    return url;
  }

  /**
   * Shopify filter/sort vormidele lisame ainult local-first oleku.
   *
   * has_local_stock true/false EI lähe vormi.
   * Seetõttu Shopify filter UI arvutab valikud kogu filtered valiku pealt.
   */
  #syncForms() {
    const section =
      this.#getSection();

    if (!section) return;

    for (
      const form of section.querySelectorAll(
        'facets-form-component form'
      )
    ) {
      if (
        !(form instanceof
          HTMLFormElement)
      ) {
        continue;
      }

      form
        .querySelectorAll(
          'input[data-local-stock-priority-hidden], input[data-image-priority-hidden]'
        )
        .forEach((input) => {
          input.remove();
        });

      this.#appendHidden(
        form,
        LOCAL_FIRST_PARAM,
        LOCAL_FIRST_VALUE
      );

      /*
       * Uue filtri või sortimise korral alustame
       * combined nimekirja lehelt 1.
       */
      this.#appendHidden(
        form,
        LOCAL_VIRTUAL_PAGE_PARAM,
        '1'
      );
    }
  }

  #appendHidden(
    form,
    name,
    value
  ) {
    const input =
      document.createElement(
        'input'
      );

    input.type = 'hidden';
    input.name = name;
    input.value = value;

    input.setAttribute(
      'data-local-stock-priority-hidden',
      ''
    );

    form.append(input);
  }

  /**
   * Shopify filter või sort teeb native AJAX section-update'i.
   *
   * Pärast seda:
   * - clear cache;
   * - mõlemad nimekirjad arvutatakse uue filtriga uuesti;
   * - virtuaalne combined leht algab page 1-st.
   */
  #observeSectionChanges() {
    const section =
      this.#getSection();

    if (
      !section ||
      this.#sectionObserver
    ) {
      return;
    }

    this.#sectionObserver =
      new MutationObserver((mutations) => {
        /*
         * Quick Add changes only the state text inside its own button.
         * The local-first observer watches the whole results subtree, so those
         * tiny UI changes must not be treated as a Shopify filter/sort refresh.
         */
        const structuralRefresh =
          mutations.some((mutation) => {
            const target =
              mutation.target;

            if (
              !(target instanceof Element) ||
              target.closest(
                '[ref="cards[]"]'
              )
            ) {
              return false;
            }

            return target.matches(
              '[ref="grid"], results-list, .collection-wrapper, facets-form-component'
            );
          });

        if (!structuralRefresh) {
          return;
        }

        if (
          this.#internalMutation
        ) {
          return;
        }

        if (!this.#isEnabled()) {
          return;
        }

        if (!this.isConnected) {
          return;
        }

        if (this.#refreshQueued) {
          return;
        }

        this.#refreshQueued = true;

        requestAnimationFrame(() => {
          this.#refreshQueued =
            false;

          if (
            this.#internalMutation ||
            !this.#isEnabled() ||
            !this.isConnected
          ) {
            return;
          }

          this.#forceActiveState();
          this.#resetPageCache();
          this.#syncForms();

          this.#initManualPagination({
            scrollToResults: false,
          });
        });
      });

    this.#sectionObserver.observe(
      section,
      {
        childList: true,
        subtree: true,
      }
    );
  }

  /**
   * LOCAL-FIRST combined nimekiri:
   *
   * Vaikimisi järjestus:
   *   [PILDIGA LOCAL]
   *   [PILDIGA REMOTE]
   *   [PILDITA LOCAL]
   *   [PILDITA REMOTE]
   *
   * Kasutaja enda sort_by valiku korral jääb pildiprioriteet välja ning
   * kasutatakse senist local -> remote järjestust.
   */
  async #initManualPagination({
    scrollToResults = false,
  } = {}) {
    if (!this.#isController()) {
      return;
    }

    if (!this.#isEnabled()) {
      return;
    }

    this.#forceActiveState();

    const token =
      ++this.#initToken;

    const section =
      this.#getSection();

    if (!section) return;

    const resultsList =
      section.querySelector(
        'results-list'
      );

    const grid =
      resultsList?.querySelector(
        '[ref="grid"]'
      );

    if (
      !(resultsList instanceof
        HTMLElement) ||
      !(grid instanceof
        HTMLUListElement)
    ) {
      return;
    }

    this.#ensureStyles();
    this.#syncForms();

    this.#disableNativeResultsPagination(
      resultsList
    );

    this.#setLoading(true);

    try {
      const phases =
        this.#getPhases();

      const declaredPageSize =
        this.#getDeclaredPageSize();

      const [
        phasePageOnes,
        nativePageOne,
      ] = await Promise.all([
        Promise.all(
          phases.map((phase) =>
            this.#fetchPhasePage(
              resultsList,
              phase,
              1,
              token
            )
          )
        ),
        declaredPageSize > 0
          ? Promise.resolve(null)
          : this.#fetchNativePageOne(
              resultsList,
              token
            ),
      ]);

      if (
        token !==
        this.#initToken
      ) {
        return;
      }

      if (
        phasePageOnes.some(
          (pageData) =>
            !pageData
        )
      ) {
        return;
      }

      this.#phaseCounts =
        new Map(
          phases.map(
            (phase, index) => [
              phase.key,
              phasePageOnes[index]
                .count,
            ]
          )
        );

      this.#localCount =
        phases.reduce(
          (total, phase, index) =>
            phase.localValue ===
            LOCAL_TRUE_VALUE
              ? total +
                phasePageOnes[index]
                  .count
              : total,
          0
        );

      this.#remoteCount =
        phases.reduce(
          (total, phase, index) =>
            phase.localValue ===
            LOCAL_FALSE_VALUE
              ? total +
                phasePageOnes[index]
                  .count
              : total,
          0
        );

      this.#totalCount =
        phasePageOnes.reduce(
          (total, pageData) =>
            total +
            pageData.count,
          0
        );

      this.#pageSize =
        this.#detectPageSize(
          nativePageOne,
          phasePageOnes,
          this.#totalCount
        );

      if (
        this.#pageSize <= 0
      ) {
        this.#pageSize = 1;
      }

      this.#totalPages =
        this.#totalCount > 0
          ? Math.ceil(
              this.#totalCount /
                this.#pageSize
            )
          : 0;

      this.#setCombinedCount(
        this.#totalCount
      );

      if (
        this.#totalPages <= 0
      ) {
        grid.replaceChildren();
        this.#removeCustomPagination();
        return;
      }

      let virtualPage =
        this.#getVirtualPage();

      virtualPage =
        Math.min(
          Math.max(
            virtualPage,
            1
          ),
          this.#totalPages
        );

      if (
        virtualPage !==
        this.#getVirtualPage()
      ) {
        history.replaceState(
          {},
          '',
          this.#urlForVirtualPage(
            virtualPage
          ).toString()
        );
      }

      const cards =
        await this.#buildCombinedPage(
          resultsList,
          virtualPage,
          token,
          phases,
          phasePageOnes
        );

      if (
        token !==
        this.#initToken
      ) {
        return;
      }

      if (!cards) return;

      await this.#prepareCardsForSwap(
        cards,
        token
      );

      if (
        token !==
        this.#initToken
      ) {
        return;
      }

      this.#renderCombinedPage(
        resultsList,
        grid,
        cards,
        virtualPage
      );

      this.#forceActiveState();

      if (scrollToResults) {
        this.#scrollToResults(
          resultsList
        );
      }
    } finally {
      if (
        token ===
        this.#initToken
      ) {
        this.#setLoading(false);
        this.#finishInitialLoad();
      }
    }
  }

  /**
   * Loe Shopify PÄRIS esimene leht ilma meie local/remote tehnilise filtrita.
   *
   * See on oluline juhul, kui kasutaja vajutab "Kohalik ladu ees" näiteks
   * Shopify page 5 või viimase pooliku lehe peal. Ekraanil oleva grid'i kaartide
   * arvu ei tohi siis kasutada products-per-page väärtusena.
   */
  async #fetchNativePageOne(
    resultsList,
    token
  ) {
    const sectionId =
      resultsList.getAttribute(
        'section-id'
      );

    if (!sectionId) {
      return null;
    }

    const url =
      this.#getBaseUrl();

    url.searchParams.set(
      'page',
      '1'
    );

    url.hash = '';

    try {
      const html =
        await sectionRenderer.getSectionHTML(
          sectionId,
          true,
          url
        );

      if (
        token !==
        this.#initToken
      ) {
        return null;
      }

      const parsed =
        new DOMParser()
          .parseFromString(
            html,
            'text/html'
          );

      const parsedGrid =
        parsed.querySelector(
          '[ref="grid"]'
        );

      if (
        !(parsedGrid instanceof
          HTMLElement)
      ) {
        return null;
      }

      const cards =
        parsedGrid.querySelectorAll(
          ':scope > [ref="cards[]"]'
        ).length;

      return {
        html,
        cards,
      };
    } catch (error) {
      console.error(
        '[Ostuklikk local-first] Native page 1 request failed:',
        error
      );

      return null;
    }
  }

  /**
   * Tuvasta Shopify tegelik products-per-page ALATI esimese lehe järgi.
   *
   * Varem kasutati hetkel ekraanil oleva grid'i kaartide arvu. Kui kasutaja
   * vajutas local-first nuppu viimasel/poolikul Shopify lehel, võis näiteks
   * 24-tootelise lehesuuruse asemel saada väärtuseks 7 või 11. See tekitas
   * poolikud combined lehed ja vahelao toodete vale jätkumise.
   */
  #detectPageSize(
    nativePageOne,
    phasePageOnes,
    totalCount
  ) {
    const declaredPageSize =
      this.#getDeclaredPageSize();

    if (declaredPageSize > 0) {
      return Math.min(
        declaredPageSize,
        Math.max(totalCount, 1)
      );
    }

    const nativeCards =
      Math.max(
        0,
        Number(
          nativePageOne?.cards
        ) || 0
      );

    if (
      totalCount > 0 &&
      nativeCards > 0
    ) {
      return Math.min(
        nativeCards,
        totalCount
      );
    }

    /*
     * Turvaline fallback juhuks, kui native page 1 AJAX päring ebaõnnestub.
     * Kui ühe faasi count on suurem kui tema page 1 kaartide arv, siis selle
     * faasi esimene leht on kindlasti täis ja annab meile täpse pageSize'i.
     */
    const phaseCards =
      phasePageOnes.map(
        (pageData) =>
          this.#cardsFromPageData(
            pageData
          ).length
      );

    for (
      let index = 0;
      index < phaseCards.length;
      index += 1
    ) {
      if (
        phaseCards[index] > 0 &&
        Number(
          phasePageOnes[index]
            ?.count || 0
        ) > phaseCards[index]
      ) {
        return phaseCards[index];
      }
    }

    const largestPhasePage =
      Math.max(
        ...phaseCards,
        0
      );

    if (
      totalCount > 0 &&
      totalCount <=
        Math.max(
          largestPhasePage,
          0
        )
    ) {
      return totalCount;
    }

    return Math.max(
      largestPhasePage,
      1
    );
  }

  /**
   * Ehita üks virtuaalne combined leht.
   *
   * Näide:
   * pageSize = 24
   * localCount = 248
   *
   * virtual page 11:
   * combined index 240...263
   *
   * local osa:
   * index 240...247 = 8 toodet
   *
   * remote osa:
   * index 0...15 = 16 toodet
   *
   * TULEMUS:
   * samal lehel 8 local + 16 remote.
   */
  async #buildCombinedPage(
    resultsList,
    virtualPage,
    token,
    phases,
    phasePageOnes
  ) {
    const combinedStart =
      (virtualPage - 1) *
      this.#pageSize;

    const combinedEnd =
      Math.min(
        combinedStart +
          this.#pageSize,
        this.#totalCount
      );

    let phaseOffset = 0;

    const requests =
      phases.map(
        (phase, index) => {
          const phaseCount =
            this.#phaseCounts.get(
              phase.key
            ) || 0;

          const phaseStart =
            Math.max(
              0,
              combinedStart -
                phaseOffset
            );

          const phaseEnd =
            Math.min(
              phaseCount,
              Math.max(
                0,
                combinedEnd -
                  phaseOffset
              )
            );

          phaseOffset +=
            phaseCount;

          if (
            phaseEnd <=
            phaseStart
          ) {
            return Promise.resolve(
              []
            );
          }

          return this.#fetchPhaseRange(
            resultsList,
            phase,
            phaseStart,
            phaseEnd,
            token,
            phasePageOnes[index]
          );
        }
      );

    const phaseCards =
      await Promise.all(
        requests
      );

    if (
      token !==
      this.#initToken
    ) {
      return null;
    }

    if (
      phaseCards.some(
        (cards) =>
          cards === null
      )
    ) {
      return null;
    }

    return phaseCards.flat();
  }

  /**
   * Too ühe lao nimekirja suvaline index-vahemik.
   *
   * Üks combined page võib pärast local/remote piiri
   * vajada näiteks remote page 1 lõppu + remote page 2 algust.
   *
   * Seetõttu fetchime vajadusel kuni 2 serveripoolset lehte
   * ning lõikame sealt täpselt õige tootevahemiku.
   */
  async #fetchPhaseRange(
    resultsList,
    phase,
    startIndex,
    endIndex,
    token,
    pageOneCache = null
  ) {
    if (
      endIndex <= startIndex
    ) {
      return [];
    }

    const firstServerPage =
      Math.floor(
        startIndex /
          this.#pageSize
      ) + 1;

    const lastServerPage =
      Math.floor(
        (endIndex - 1) /
          this.#pageSize
      ) + 1;

    const requests = [];

    for (
      let page =
        firstServerPage;
      page <=
        lastServerPage;
      page += 1
    ) {
      if (
        page === 1 &&
        pageOneCache
      ) {
        requests.push(
          Promise.resolve(
            pageOneCache
          )
        );
      } else {
        requests.push(
          this.#fetchPhasePage(
            resultsList,
            phase,
            page,
            token
          )
        );
      }
    }

    const pages =
      await Promise.all(
        requests
      );

    if (
      token !==
      this.#initToken
    ) {
      return null;
    }

    if (
      pages.some(
        (pageData) =>
          !pageData
      )
    ) {
      return null;
    }

    const allCards = [];

    for (
      const pageData of pages
    ) {
      allCards.push(
        ...this.#cardsFromPageData(
          pageData
        )
      );
    }

    const fetchedRangeStart =
      (firstServerPage - 1) *
      this.#pageSize;

    const sliceStart =
      Math.max(
        0,
        startIndex -
          fetchedRangeStart
      );

    const sliceEnd =
      Math.max(
        sliceStart,
        endIndex -
          fetchedRangeStart
      );

    return allCards.slice(
      sliceStart,
      sliceEnd
    );
  }

  /**
   * Serveripoolne ühe faasi üks leht.
   *
   * Base URL sisaldab kõiki kasutaja filtreid.
   * Lisatakse ainult sisemine lao-filter + päris page number.
   *
   * Vastus cache'itakse HTML-na, mitte DOM node'idena,
   * et samu lehti saaks turvaliselt edasi-tagasi kasutada.
   */
  async #fetchPhasePage(
    resultsList,
    phase,
    page,
    token
  ) {
    const sectionId =
      resultsList.getAttribute(
        'section-id'
      );

    if (!sectionId) {
      return null;
    }

    const cacheKey =
      this.#getCacheKey(
        phase,
        page
      );

    if (
      this.#pageCache.has(
        cacheKey
      )
    ) {
      return this.#pageCache.get(
        cacheKey
      );
    }

    const url =
      this.#getPhaseUrl(
        phase
      );

    url.searchParams.set(
      'page',
      String(page)
    );

    url.hash = '';

    try {
      const html =
        await sectionRenderer.getSectionHTML(
          sectionId,
          true,
          url
        );

      if (
        token !==
        this.#initToken
      ) {
        return null;
      }

      const parsed =
        new DOMParser()
          .parseFromString(
            html,
            'text/html'
          );

      const parsedGrid =
        parsed.querySelector(
          '[ref="grid"]'
        );

      const facets =
        parsed.querySelector(
          'facets-form-component[data-results-count]'
        );

      const count =
        Math.max(
          0,
          Number.parseInt(
            facets?.getAttribute(
              'data-results-count'
            ) || '0',
            10
          ) || 0
        );

      let lastPage = 0;

      if (
        count > 0 &&
        parsedGrid instanceof
          HTMLElement
      ) {
        lastPage =
          Math.max(
            1,
            Number.parseInt(
              parsedGrid.dataset.lastPage ||
                '1',
              10
            ) || 1
          );
      }

      const pageData = {
        html,
        count,
        lastPage,
      };

      this.#pageCache.set(
        cacheKey,
        pageData
      );

      return pageData;
    } catch (error) {
      console.error(
        '[Ostuklikk local-first] Phase request failed:',
        error
      );

      return null;
    }
  }

  /**
   * Iga kasutus saab värsked DOM node'id.
   * See väldib sama card node'i ümbertõstmise probleeme.
   */
  #cardsFromPageData(
    pageData
  ) {
    if (!pageData?.html) {
      return [];
    }

    const parsed =
      new DOMParser()
        .parseFromString(
          pageData.html,
          'text/html'
        );

    const parsedGrid =
      parsed.querySelector(
        '[ref="grid"]'
      );

    if (
      !(parsedGrid instanceof
        HTMLElement)
    ) {
      return [];
    }

    return Array.from(
      parsedGrid.querySelectorAll(
        ':scope > [ref="cards[]"]'
      )
    );
  }

  /**
   * Manuaalne virtuaalse lehe vahetus:
   * - EI reloadi kogu lehte;
   * - EI kuva vahepeal kategooria esimest lehte;
   * - ehitab kohe local+remote combined slice'i.
   */
  async #navigateVirtualPage(
    virtualPage,
    {
      pushHistory = true,
      scrollToResults = true,
    } = {}
  ) {
    if (!this.#isEnabled()) {
      return;
    }

    this.#forceActiveState();

    if (
      this.#totalPages <= 0
    ) {
      return;
    }

    const safePage =
      Math.min(
        Math.max(
          Number(virtualPage) || 1,
          1
        ),
        this.#totalPages
      );

    const section =
      this.#getSection();

    const resultsList =
      section?.querySelector(
        'results-list'
      );

    const grid =
      resultsList?.querySelector(
        '[ref="grid"]'
      );

    if (
      !(resultsList instanceof
        HTMLElement) ||
      !(grid instanceof
        HTMLUListElement)
    ) {
      return;
    }

    // Cached target pages swap synchronously, before scrolling or yielding.
    const readyCards = this.#combinedPageCache.get(safePage);
    if (readyCards) {
      ++this.#initToken;
      if (pushHistory) history.pushState({}, '', this.#urlForVirtualPage(safePage).toString());
      this.#renderCombinedPage(resultsList, grid, readyCards.map(card => card.cloneNode(true)), safePage);
      this.#setLoading(false);
      this.#finishInitialLoad();
      if (scrollToResults) this.#scrollToResults(resultsList);
      return;
    }
    // Never scroll the old page into view while waiting for the target.
    this.#setLoading(true);

    /*
     * Vii kasutaja KOHE kliki hetkel lehe algusesse.
     * Ära oota AJAX päringu ega uue grid'i renderdamise lõppu.
     */
    if (scrollToResults) {
      this.#scrollToResults(
        resultsList
      );
    }

    const token =
      ++this.#initToken;

    this.#setLoading(true);

    try {
      const phases =
        this.#getPhases();

      const phasePageOnes =
        await Promise.all(
          phases.map((phase) =>
            this.#fetchPhasePage(
              resultsList,
              phase,
              1,
              token
            )
          )
        );

      if (
        token !==
        this.#initToken
      ) {
        return;
      }

      if (
        phasePageOnes.some(
          (pageData) =>
            !pageData
        )
      ) {
        return;
      }

      const preparedCards =
        this.#combinedPageCache.get(
          safePage
        );

      const cards = preparedCards
        ? preparedCards.map((card) =>
            card.cloneNode(true)
          )
        : await this.#buildCombinedPage(
            resultsList,
            safePage,
            token,
            phases,
            phasePageOnes
          );

      if (
        token !==
        this.#initToken
      ) {
        return;
      }

      if (!cards) return;

      if (!preparedCards) {
        await this.#prepareCardsForSwap(
          cards,
          token
        );
      }

      if (
        token !==
        this.#initToken
      ) {
        return;
      }

      if (pushHistory) {
        history.pushState(
          {},
          '',
          this.#urlForVirtualPage(
            safePage
          ).toString()
        );
      }

      /*
       * Leheküljenumber võib liikuda juba täielikult vahelao alasse,
       * kuid local-first režiim peab endiselt visuaalselt aktiivne olema.
       */
      this.#forceActiveState();

      this.#renderCombinedPage(
        resultsList,
        grid,
        cards,
        safePage
      );

      this.#forceActiveState();

      if (scrollToResults) {
        this.#scrollToResults(
          resultsList
        );
      }
    } finally {
      if (
        token ===
        this.#initToken
      ) {
        this.#setLoading(false);
        this.#finishInitialLoad();
      }
    }
  }

  async #prepareCardsForSwap(
    cards,
    token
  ) {
    const images = cards
      .slice(0, 12)
      .map((card) =>
        card.querySelector('img')
      )
      .filter(
        (image) =>
          image instanceof
            HTMLImageElement
      );

    if (images.length === 0) {
      return;
    }

    const preload = (image) =>
      new Promise((resolve) => {
        const loader = new Image();
        const finish = () => {
          if (
            typeof loader.decode ===
            'function'
          ) {
            loader
              .decode()
              .catch(() => {})
              .finally(resolve);
          } else {
            resolve();
          }
        };

        loader.onload = finish;
        loader.onerror = resolve;
        loader.decoding = 'async';

        const sizes =
          image.getAttribute('sizes');
        const srcset =
          image.getAttribute('srcset');

        if (sizes) loader.sizes = sizes;
        if (srcset) loader.srcset = srcset;

        loader.src =
          image.currentSrc ||
          image.src;

        if (loader.complete) finish();
      });

    await Promise.race([
      Promise.all(
        images.map(preload)
      ),
      new Promise((resolve) =>
        setTimeout(resolve, 800)
      ),
    ]);

    if (
      token !==
      this.#initToken
    ) {
      return;
    }
  }

  #renderCombinedPage(
    resultsList,
    grid,
    cards,
    virtualPage
  ) {
    this.#internalMutation =
      true;

    try {
      grid.replaceChildren(
        ...cards
      );

      this.#finishInitialLoad();

      /*
       * Native pagination ei tohi local-first ajal
       * custom paginationiga võistelda.
       */
      grid.dataset.lastPage =
        '1';

      this.#removeCustomPagination();

      this.#renderManualPagination(
        resultsList,
        virtualPage
      );

      this.#warmAdjacentPage(
        resultsList,
        virtualPage
      );

      this.#saveReturnPage(grid);
    } finally {
      requestAnimationFrame(() => {
        this.#internalMutation =
          false;
      });
    }
  }

  /**
   * Browseri URL on phase-free:
   *
   * ?sort_by=price-ascending&local_first=1&local_page=11
   *
   * Õige local/remote slice laetakse AJAX-iga.
   */
  #urlForVirtualPage(
    virtualPage
  ) {
    const safePage =
      Math.min(
        Math.max(
          Number(virtualPage) || 1,
          1
        ),
        Math.max(
          this.#totalPages,
          1
        )
      );

    const url =
      this.#getBaseUrl();

    url.searchParams.set(
      LOCAL_FIRST_PARAM,
      LOCAL_FIRST_VALUE
    );

    url.searchParams.set(
      LOCAL_VIRTUAL_PAGE_PARAM,
      String(safePage)
    );

    return url;
  }

  #setCombinedCount(
    totalCount
  ) {
    const section =
      this.#getSection();

    if (!section) return;

    section
      .querySelectorAll(
        '.products-count-wrapper span'
      )
      .forEach((element) => {
        const currentText =
          element.textContent?.trim() ||
          '';

        if (/\d/.test(currentText)) {
          element.textContent =
            currentText.replace(
              /[\d\s.,]+/,
              String(totalCount)
            );

          return;
        }

        const lang =
          (
            document.documentElement.lang ||
            'en'
          ).toLowerCase();

        if (
          lang.startsWith('et')
        ) {
          element.textContent =
            `${totalCount} toodet`;
        } else if (
          lang.startsWith('fi')
        ) {
          element.textContent =
            `${totalCount} tuotetta`;
        } else if (
          lang.startsWith('ru')
        ) {
          element.textContent =
            `${totalCount} товаров`;
        } else {
          element.textContent =
            `${totalCount} products`;
        }
      });
  }

  #disableNativeResultsPagination(
    resultsList
  ) {
    if (
      resultsList.infinityScrollObserver
        instanceof
        IntersectionObserver
    ) {
      resultsList.infinityScrollObserver.disconnect();
    }

    if (
      resultsList.pages instanceof Map
    ) {
      resultsList.pages.clear();
    }

    resultsList
      .querySelectorAll(
        '[ref="viewMorePrevious"], [ref="viewMoreNext"]'
      )
      .forEach((element) => {
        element.remove();
      });

    this.#hideNativePaginationAndJump();
  }

  #hideNativePaginationAndJump() {
    const section =
      this.#getSection();

    if (!section) return;

    section
      .querySelectorAll(
        'pagination-component, .pagination'
      )
      .forEach((element) => {
        if (
          element.closest(
            '[data-local-priority-pagination]'
          )
        ) {
          return;
        }

        if (
          element instanceof
          HTMLElement
        ) {
          element.style.setProperty(
            'display',
            'none',
            'important'
          );

          element.setAttribute(
            'data-local-priority-native-hidden',
            ''
          );
        }
      });

    const pageJump =
      document.querySelector(
        '[data-ostuklik-page-jump]'
      );

    if (
      pageJump instanceof
      HTMLElement
    ) {
      pageJump.style.setProperty(
        'display',
        'none',
        'important'
      );

      pageJump.setAttribute(
        'data-local-priority-native-hidden',
        ''
      );
    }
  }

  #removeCustomPagination() {
    const section =
      this.#getSection();

    section
      ?.querySelectorAll(
        '[data-local-priority-pagination]'
      )
      .forEach((element) => {
        element.remove();
      });
  }

  #renderManualPagination(
    resultsList,
    currentPage
  ) {
    const wrapper =
      document.createElement('div');

    wrapper.className =
      'local-priority-pagination';

    wrapper.setAttribute(
      'data-local-priority-pagination',
      ''
    );

    /*
     * Diagnostika:
     * browseri devtoolsist saab vajadusel näha:
     * - page size
     * - local count
     * - remote count
     */
    wrapper.dataset.pageSize =
      String(this.#pageSize);

    wrapper.dataset.localCount =
      String(this.#localCount);

    wrapper.dataset.remoteCount =
      String(this.#remoteCount);

    wrapper.dataset.imageLocalCount =
      String(
        this.#phaseCounts.get(
          'image-local'
        ) || 0
      );

    wrapper.dataset.imageRemoteCount =
      String(
        this.#phaseCounts.get(
          'image-remote'
        ) || 0
      );

    wrapper.dataset.imagelessLocalCount =
      String(
        this.#phaseCounts.get(
          'imageless-local'
        ) || 0
      );

    wrapper.dataset.imagelessRemoteCount =
      String(
        this.#phaseCounts.get(
          'imageless-remote'
        ) || 0
      );

    const nav =
      document.createElement('nav');

    nav.className =
      'local-priority-pagination__nav';

    nav.setAttribute(
      'aria-label',
      this.#paginationAriaLabel()
    );

    if (
      currentPage > 1
    ) {
      nav.append(
        this.#makePageControl(
          currentPage - 1,
          '‹',
          'local-priority-pagination__arrow',
          this.#previousLabel()
        )
      );
    } else {
      nav.append(
        this.#makeDisabledArrow(
          '‹'
        )
      );
    }

    const items =
      this.#buildPageItems(
        currentPage,
        this.#totalPages
      );

    for (
      const item of items
    ) {
      if (
        item === 'ellipsis'
      ) {
        const ellipsis =
          document.createElement(
            'span'
          );

        ellipsis.className =
          'local-priority-pagination__ellipsis';

        ellipsis.textContent =
          '…';

        ellipsis.setAttribute(
          'aria-hidden',
          'true'
        );

        nav.append(
          ellipsis
        );

        continue;
      }

      if (
        item ===
        currentPage
      ) {
        const current =
          document.createElement(
            'span'
          );

        current.className =
          'local-priority-pagination__page local-priority-pagination__page--current';

        current.textContent =
          String(item);

        current.setAttribute(
          'aria-current',
          'page'
        );

        nav.append(
          current
        );

        continue;
      }

      nav.append(
        this.#makePageControl(
          item,
          String(item),
          'local-priority-pagination__page'
        )
      );
    }

    if (
      currentPage <
      this.#totalPages
    ) {
      nav.append(
        this.#makePageControl(
          currentPage + 1,
          '›',
          'local-priority-pagination__arrow',
          this.#nextLabel()
        )
      );
    } else {
      nav.append(
        this.#makeDisabledArrow(
          '›'
        )
      );
    }

    wrapper.append(nav);

    if (
      this.#totalPages >= 2
    ) {
      wrapper.append(
        this.#makePageJump(
          currentPage
        )
      );
    }

    resultsList.append(
      wrapper
    );
  }

  #makePageControl(
    page,
    text,
    className,
    ariaLabel = ''
  ) {
    const link =
      document.createElement(
        'a'
      );

    link.className =
      className;

    link.textContent =
      text;

    link.href =
      this.#urlForVirtualPage(
        page
      ).toString();

    if (ariaLabel) {
      link.setAttribute(
        'aria-label',
        ariaLabel
      );
    }

    link.addEventListener(
      'click',
      (event) => {
        /*
         * Ctrl/Cmd click, middle click jne jäävad browseri tavakäitumiseks.
         */
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        event.preventDefault();

        this.#navigateVirtualPage(
          page,
          {
            pushHistory: true,
            scrollToResults: true,
          }
        );
      }
    );

    return link;
  }

  #makeDisabledArrow(
    text
  ) {
    const span =
      document.createElement(
        'span'
      );

    span.className =
      'local-priority-pagination__arrow local-priority-pagination__arrow--disabled';

    span.textContent =
      text;

    span.setAttribute(
      'aria-hidden',
      'true'
    );

    return span;
  }

  #buildPageItems(
    currentPage,
    totalPages
  ) {
    if (
      totalPages <= 7
    ) {
      return Array.from(
        {
          length:
            totalPages,
        },
        (_, index) =>
          index + 1
      );
    }

    const pages =
      new Set([
        1,
        totalPages,
        currentPage - 1,
        currentPage,
        currentPage + 1,
      ]);

    if (
      currentPage <= 3
    ) {
      pages.add(2);
      pages.add(3);
    }

    if (
      currentPage >=
      totalPages - 2
    ) {
      pages.add(
        totalPages - 1
      );

      pages.add(
        totalPages - 2
      );
    }

    const sorted =
      [...pages]
        .filter(
          (page) =>
            page >= 1 &&
            page <= totalPages
        )
        .sort(
          (a, b) =>
            a - b
        );

    const output = [];

    for (
      let index = 0;
      index <
        sorted.length;
      index += 1
    ) {
      const page =
        sorted[index];

      const previous =
        sorted[
          index - 1
        ];

      if (
        index > 0 &&
        page - previous >
          1
      ) {
        output.push(
          'ellipsis'
        );
      }

      output.push(
        page
      );
    }

    return output;
  }

  #makePageJump(
    currentPage
  ) {
    const container =
      document.createElement(
        'div'
      );

    container.className =
      'local-priority-pagination__jump';

    const label =
      document.createElement(
        'label'
      );

    label.className =
      'local-priority-pagination__jump-label';

    label.textContent =
      this.#jumpLabel();

    const input =
      document.createElement(
        'input'
      );

    input.className =
      'local-priority-pagination__jump-input';

    input.type =
      'number';

    input.inputMode =
      'numeric';

    input.min =
      '1';

    input.max =
      String(
        this.#totalPages
      );

    input.placeholder =
      `1–${this.#totalPages}`;

    input.setAttribute(
      'aria-label',
      this.#jumpAriaLabel()
    );

    const button =
      document.createElement(
        'button'
      );

    button.className =
      'local-priority-pagination__jump-button';

    button.type =
      'button';

    button.textContent =
      this.#jumpButtonLabel();

    const go = () => {
      const target =
        Number.parseInt(
          input.value,
          10
        );

      if (
        !Number.isInteger(
          target
        ) ||
        target < 1 ||
        target >
          this.#totalPages
      ) {
        input.focus();
        return;
      }

      if (
        target ===
        currentPage
      ) {
        return;
      }

      this.#navigateVirtualPage(
        target,
        {
          pushHistory: true,
          scrollToResults: true,
        }
      );
    };

    button.addEventListener(
      'click',
      go
    );

    input.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key !==
          'Enter'
        ) {
          return;
        }

        event.preventDefault();
        go();
      }
    );

    container.append(
      label,
      input,
      button
    );

    return container;
  }

  #getDeclaredPageSize() {
    const sorter =
      this.#getSection()?.querySelector(
        'image-priority-sorter[data-server-page-size]'
      );

    return Math.max(
      0,
      Number.parseInt(
        this.#getSection()?.querySelector('results-list')?.dataset.resultsPageSize ||
          sorter?.dataset.serverPageSize || '0',
        10
      ) || 0
    );
  }

  #warmAdjacentPage(
    resultsList,
    currentPage
  ) {
    const nextPage =
      Number(currentPage) + 1;

    if (
      nextPage > this.#totalPages ||
      this.#warmingPages.has(nextPage)
    ) {
      return;
    }

    this.#warmingPages.add(nextPage);

    const warm = async () => {
      if (
        !this.isConnected ||
        !this.#isEnabled() ||
        nextPage <= this.#getVirtualPage()
      ) {
        this.#warmingPages.delete(
          nextPage
        );
        return;
      }

      const token = this.#initToken;
      const phases = this.#getPhases();
      const phasePageOnes =
        phases.map((phase) =>
          this.#pageCache.get(
            this.#getCacheKey(
              phase,
              1
            )
          ) || null
        );

      try {
        const cards =
          await this.#buildCombinedPage(
            resultsList,
            nextPage,
            token,
            phases,
            phasePageOnes
          );

        if (
          cards &&
          token === this.#initToken
        ) {
          await this.#prepareCardsForSwap(
            cards,
            token
          );

          if (token === this.#initToken) {
            this.#combinedPageCache.set(
              nextPage,
              cards.map((card) =>
                card.cloneNode(true)
              )
            );
          }
        }
      } finally {
        this.#warmingPages.delete(
          nextPage
        );
      }
    };

    setTimeout(
      () => void warm(),
      250
    );
  }

  #setLoading(
    loading
  ) {
    this.#getSection()?.querySelector('results-list')?.setAttribute('aria-busy', String(Boolean(loading)));
    document.documentElement.classList.toggle(
      'local-stock-priority-loading',
      Boolean(loading)
    );
  }

  #finishInitialLoad() {
    document.documentElement.classList.remove(
      'local-priority-initializing'
    );
  }

  #scrollToResults(
    resultsList
  ) {
    /*
     * Horizon theme kasutab oma scroll-container utiliiti.
     *
     * Tavaline window.scrollTo() ei pruugi siin olla õige scroll-container,
     * mistõttu eelmine versioon ei viinud kasutajat alati üles.
     *
     * Kasutame nüüd sama @theme/scroll-container scrollTo() funktsiooni,
     * mida kasutab Shopify enda paginated-list.js.
     */
    const forceTop = () => {
      try {
        themeScrollTo({
          top: 0,
          behavior: 'instant',
        });
      } catch (error) {
        console.warn(
          '[Ostuklikk local-first] Theme scrollTo failed, using fallback:',
          error
        );
      }

      /*
       * Browseri fallback juhuks, kui storefront kasutab tavapärast
       * document scrollingElement'i.
       */
      const scrollingElement =
        document.scrollingElement;

      if (
        scrollingElement instanceof
        HTMLElement
      ) {
        scrollingElement.scrollTop = 0;
      }

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
    };

    if (
      'scrollRestoration' in history
    ) {
      history.scrollRestoration =
        'manual';
    }

    /*
     * 1. Kohe paginationi kliki hetkel.
     */
    forceTop();

    /*
     * 2. Pärast järgmist browseri render frame'i.
     */
    requestAnimationFrame(() => {
      forceTop();
    });

    /*
     * 3. Pärast product grid'i/layout'i stabiliseerumist.
     */
    setTimeout(
      forceTop,
      120
    );

    setTimeout(
      forceTop,
      350
    );
  }

  #lang() {
    return (
      document.documentElement.lang ||
      'en'
    ).toLowerCase();
  }

  #paginationAriaLabel() {
    if (
      this.#lang()
        .startsWith('et')
    ) {
      return 'Lehekülgede navigeerimine';
    }

    if (
      this.#lang()
        .startsWith('fi')
    ) {
      return 'Sivunavigointi';
    }

    if (
      this.#lang()
        .startsWith('ru')
    ) {
      return 'Навигация по страницам';
    }

    return 'Pagination';
  }

  #previousLabel() {
    if (
      this.#lang()
        .startsWith('et')
    ) {
      return 'Eelmine leht';
    }

    if (
      this.#lang()
        .startsWith('fi')
    ) {
      return 'Edellinen sivu';
    }

    if (
      this.#lang()
        .startsWith('ru')
    ) {
      return 'Предыдущая страница';
    }

    return 'Previous page';
  }

  #nextLabel() {
    if (
      this.#lang()
        .startsWith('et')
    ) {
      return 'Järgmine leht';
    }

    if (
      this.#lang()
        .startsWith('fi')
    ) {
      return 'Seuraava sivu';
    }

    if (
      this.#lang()
        .startsWith('ru')
    ) {
      return 'Следующая страница';
    }

    return 'Next page';
  }

  #jumpLabel() {
    if (
      this.#lang()
        .startsWith('et')
    ) {
      return 'Mine lehele:';
    }

    if (
      this.#lang()
        .startsWith('fi')
    ) {
      return 'Siirry sivulle:';
    }

    if (
      this.#lang()
        .startsWith('ru')
    ) {
      return 'Перейти на страницу:';
    }

    return 'Go to page:';
  }

  #jumpButtonLabel() {
    if (
      this.#lang()
        .startsWith('et')
    ) {
      return 'Mine';
    }

    if (
      this.#lang()
        .startsWith('fi')
    ) {
      return 'Siirry';
    }

    if (
      this.#lang()
        .startsWith('ru')
    ) {
      return 'Перейти';
    }

    return 'Go';
  }

  #jumpAriaLabel() {
    if (
      this.#lang()
        .startsWith('et')
    ) {
      return 'Mine lehele';
    }

    if (
      this.#lang()
        .startsWith('fi')
    ) {
      return 'Siirry sivulle';
    }

    if (
      this.#lang()
        .startsWith('ru')
    ) {
      return 'Перейти на страницу';
    }

    return 'Go to page';
  }

  #ensureStyles() {
    if (
      document.getElementById(
        'local-priority-pagination-styles'
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.id =
      'local-priority-pagination-styles';

    style.textContent = `
      .local-stock-priority-active [data-ostuklik-page-jump] {
        display: none !important;
      }

      .local-priority-pagination {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        margin-top: 30px;
        margin-bottom: 10px;
        gap: 22px;
      }

      .local-priority-pagination__nav {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        min-height: 38px;
      }

      .local-priority-pagination__page,
      .local-priority-pagination__arrow,
      .local-priority-pagination__ellipsis {
        box-sizing: border-box;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 32px;
        height: 32px;
        padding: 0 7px;
        border-radius: 6px;
        color: inherit;
        font: inherit;
        font-size: 14px;
        line-height: 1;
        text-decoration: none;
      }

      .local-priority-pagination__page:hover,
      .local-priority-pagination__arrow:hover {
        background: rgb(var(--color-foreground-rgb) / 0.07);
      }

      .local-priority-pagination__page--current {
        background: #111111;
        color: #ffffff;
        font-weight: 600;
      }

      .local-priority-pagination__arrow {
        font-size: 24px;
        font-weight: 300;
      }

      .local-priority-pagination__arrow--disabled {
        opacity: 0.25;
        pointer-events: none;
      }

      .local-priority-pagination__ellipsis {
        min-width: 24px;
        padding-inline: 2px;
      }

      .local-priority-pagination__jump {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        flex-wrap: wrap;
      }

      .local-priority-pagination__jump-label {
        font-size: 13px;
        font-weight: 600;
      }

      .local-priority-pagination__jump-input {
        box-sizing: border-box;
        width: 64px;
        height: 38px;
        padding: 0 8px;
        border: 1px solid rgb(var(--color-foreground-rgb) / 0.20);
        border-radius: 6px;
        background: #ffffff;
        color: #111111;
        font: inherit;
        font-size: 14px;
        text-align: center;
        appearance: textfield;
        -moz-appearance: textfield;
      }

      .local-priority-pagination__jump-input::-webkit-inner-spin-button,
      .local-priority-pagination__jump-input::-webkit-outer-spin-button {
        margin: 0;
        -webkit-appearance: none;
      }

      .local-priority-pagination__jump-button {
        box-sizing: border-box;
        height: 38px;
        padding: 0 17px;
        border: 1px solid #111111;
        border-radius: 6px;
        background: #111111;
        color: #ffffff;
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }

      .local-stock-priority-loading results-list [ref="grid"] {
        visibility: hidden;
        pointer-events: none;
      }

      @media screen and (max-width: 749px) {
        .local-priority-pagination {
          margin-top: 24px;
          gap: 16px;
        }

        .local-priority-pagination__page,
        .local-priority-pagination__arrow,
        .local-priority-pagination__ellipsis {
          min-width: 30px;
          height: 30px;
          font-size: 13px;
        }

        .local-priority-pagination__arrow {
          font-size: 21px;
        }

        .local-priority-pagination__jump-input,
        .local-priority-pagination__jump-button {
          height: 36px;
        }
      }
    `;

    document.head.append(
      style
    );
  }
}

if (
  !customElements.get(
    'local-stock-priority-toggle'
  )
) {
  customElements.define(
    'local-stock-priority-toggle',
    LocalStockPriorityToggle
  );
}
